import { useEffect, useState } from "react";
import { Upload, FileText, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useChatPlusStore } from "../store/chatPlusStore";
import * as api from "../services/api";
import { toast } from "sonner";

export function DocumentPanel() {
  const {
    documents,
    activeConversationId,
    setDocuments,
    addDocument,
    removeDocument,
  } = useChatPlusStore();

  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (activeConversationId) {
      loadDocuments();
    } else {
      setDocuments([]);
    }
  }, [activeConversationId]);

  const loadDocuments = async () => {
    if (!activeConversationId) return;

    try {
      const data = await api.fetchDocuments(activeConversationId);
      setDocuments(data);
    } catch (error) {
      console.error("Erro ao carregar documentos:", error);
      toast.error("Erro ao carregar documentos");
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !activeConversationId) return;

    if (file.type !== 'application/pdf') {
      toast.error("Apenas arquivos PDF são suportados");
      return;
    }

    try {
      setIsUploading(true);
      const document = await api.uploadDocument(activeConversationId, file);
      addDocument(document);
      toast.success("Documento enviado com sucesso");
    } catch (error) {
      console.error("Erro ao enviar documento:", error);
      toast.error("Erro ao enviar documento");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteDocument = async (id: string) => {
    try {
      await api.deleteDocument(id);
      removeDocument(id);
      toast.success("Documento excluído");
    } catch (error) {
      console.error("Erro ao excluir documento:", error);
      toast.error("Erro ao excluir documento");
    }
  };

  if (!activeConversationId) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <p className="text-sm">Selecione uma conversa</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full border-l bg-background">
      <div className="p-4 border-b">
        <h3 className="font-semibold mb-3">Documentos</h3>
        
        <label>
          <input
            type="file"
            accept=".pdf"
            onChange={handleFileSelect}
            disabled={isUploading}
            className="hidden"
          />
          <Button
            variant="outline"
            className="w-full"
            size="sm"
            disabled={isUploading}
            asChild
          >
            <span>
              {isUploading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Anexar PDF
            </span>
          </Button>
        </label>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {documents.map((doc) => (
          <div
            key={doc.id}
            className="flex items-center justify-between p-2 rounded-lg hover:bg-accent mb-1"
          >
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <FileText className="w-4 h-4 shrink-0" />
              <span className="text-sm truncate">{doc.filename}</span>
            </div>

            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 shrink-0"
              onClick={() => handleDeleteDocument(doc.id)}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        ))}

        {documents.length === 0 && (
          <div className="text-center text-muted-foreground mt-8">
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Nenhum documento anexado</p>
          </div>
        )}
      </div>
    </div>
  );
}
